import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, RefreshCw, AlertOctagon, AlertTriangle, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SP_COLOR = {
  1: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.35)', fg: '#D97706', label: 'SP1' },
  2: { bg: 'rgba(234,88,12,0.12)',  border: 'rgba(234,88,12,0.40)',  fg: '#C2410C', label: 'SP2' },
  3: { bg: 'rgba(220,38,38,0.15)',  border: 'rgba(220,38,38,0.45)',  fg: '#B91C1C', label: 'SP3' },
};

function fmt(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString([], { year: '2-digit', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function SpBadge({ level, testId }) {
  const c = SP_COLOR[level] || SP_COLOR[1];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.fg }}
      data-testid={testId}
    >
      {level >= 3 ? <AlertOctagon className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {c.label}
    </span>
  );
}

export default function AdminChatViolationsPanel({ API, token, lang }) {
  const isID = lang !== 'en';
  const [summary, setSummary] = useState({ offenders: [], total_violations: 0 });
  const [violations, setViolations] = useState({ violations: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOffender, setDetailOffender] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, v] = await Promise.all([
        axios.get(`${API}/admin/chat/violations/summary`, { headers }),
        axios.get(`${API}/admin/chat/violations?limit=50`, { headers }),
      ]);
      setSummary(s.data);
      setViolations(v.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (offender) => {
    setDetailOffender(offender);
    setDetailRows([]);
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chat/violations`, {
        params: { account_id: offender.account_id, limit: 200 },
        headers,
      });
      setDetailRows(r.data.violations || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const spCounts = summary.offenders.reduce((acc, o) => {
    acc[o.sp_level] = (acc[o.sp_level] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="glass-card rounded-xl p-4 mb-6" data-testid="admin-chat-violations">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#D98A94]" />
          <h3 className="text-sm font-bold t-text">
            {isID ? 'Pelanggaran Chat (SP1 / SP2 / SP3)' : 'Chat Violations (SP1 / SP2 / SP3)'}
          </h3>
          <span className="text-xs t-text-muted">
            {summary.total_violations} {isID ? 'total pelanggaran' : 'total violations'} · {summary.offenders.length} {isID ? 'pelanggar' : 'offenders'}
          </span>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="chat-violations-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {isID ? 'Refresh' : 'Refresh'}
        </Button>
      </div>

      {/* SP tally */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[1, 2, 3].map(lvl => {
          const c = SP_COLOR[lvl];
          return (
            <div key={lvl} className="rounded-lg p-3 text-center" style={{ background: c.bg, border: `1px solid ${c.border}` }} data-testid={`sp-tally-${lvl}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: c.fg }}>{c.label}</div>
              <div className="text-2xl font-bold t-text tabular-nums mt-0.5">{spCounts[lvl] || 0}</div>
              <div className="text-[10px] t-text-muted">
                {lvl === 3 ? (isID ? 'Perlu tinjau' : 'Needs review') : (isID ? 'Pelanggar' : 'Offenders')}
              </div>
            </div>
          );
        })}
      </div>

      {error && <div className="text-xs text-red-500 p-2 rounded bg-red-500/10 border border-red-500/25 mb-3">{error}</div>}

      {/* Offenders roll-up */}
      {summary.offenders.length > 0 ? (
        <div className="mb-5">
          <div className="text-[10px] t-text-muted uppercase tracking-wider mb-2 px-1">{isID ? 'Daftar pelanggar' : 'Offenders'}</div>
          <div className="space-y-1.5">
            {summary.offenders.map(o => (
              <button key={o.account_id} onClick={() => openDetail(o)} data-testid={`offender-${o.account_id}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg t-bg-input hover:bg-[var(--bg-input-hover)] transition text-left">
                <SpBadge level={o.sp_level} testId={`offender-sp-${o.account_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold t-text truncate">{o.author_name || 'Member'}</div>
                  <div className="text-[10px] t-text-muted truncate">
                    {o.author_whatsapp} · {o.category || '-'} · {isID ? 'terakhir' : 'last'}: {fmt(o.last_at)} · {o.last_group_city}
                  </div>
                </div>
                <span className="text-xs t-text font-bold whitespace-nowrap">
                  {o.count} {isID ? 'kali' : o.count > 1 ? 'offenses' : 'offense'}
                </span>
                <ChevronRight className="w-4 h-4 t-text-muted" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        !loading && <div className="text-xs t-text-muted text-center py-3">{isID ? 'Belum ada pelanggaran tercatat.' : 'No violations recorded.'}</div>
      )}

      {/* Recent violations feed */}
      {violations.violations.length > 0 && (
        <div>
          <div className="text-[10px] t-text-muted uppercase tracking-wider mb-2 px-1">{isID ? 'Riwayat terbaru' : 'Recent'}</div>
          <div className="max-h-[400px] overflow-y-auto space-y-1.5">
            {violations.violations.map(v => (
              <div key={v.id} className="p-2.5 rounded-lg t-bg-input" data-testid={`violation-${v.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <SpBadge level={v.sp_level} />
                    <span className="text-xs font-semibold t-text">{v.author_name}</span>
                    <span className="text-[10px] t-text-muted">· {v.category}</span>
                  </div>
                  <span className="text-[10px] t-text-muted whitespace-nowrap">{fmt(v.created_at)}</span>
                </div>
                <div className="text-[11px] t-text-muted mb-1">
                  <b>{v.violation_label_id}</b> · {isID ? 'grup' : 'group'} <b className="t-text">{v.group_city}</b> · scope <b>{v.scope}</b>
                </div>
                <div className="text-xs t-text bg-[var(--bg-elevated)] rounded p-2 italic break-words">
                  &ldquo;{v.attempted_text}&rdquo;
                </div>
                {v.matched_terms?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {v.matched_terms.map((t, i) => (
                      <span key={`${v.id}-term-${i}`} className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 font-mono">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-offender detail */}
      <Dialog open={!!detailOffender} onOpenChange={v => !v && setDetailOffender(null)}>
        <DialogContent className="glass-card max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              {detailOffender && <SpBadge level={detailOffender.sp_level} />}
              {detailOffender?.author_name}
            </DialogTitle>
          </DialogHeader>
          {detailOffender && (
            <div className="text-xs t-text-muted mb-3">
              {detailOffender.author_whatsapp} · {detailOffender.category || '-'} · {detailOffender.count} {isID ? 'pelanggaran' : 'violations'}
            </div>
          )}
          {detailLoading ? (
            <div className="text-center text-sm t-text-muted py-4">Loading...</div>
          ) : (
            <div className="space-y-2">
              {detailRows.map(v => (
                <div key={v.id} className="p-2.5 rounded-lg t-bg-input">
                  <div className="flex items-center justify-between mb-1">
                    <SpBadge level={v.sp_level} />
                    <span className="text-[10px] t-text-muted">{fmt(v.created_at)}</span>
                  </div>
                  <div className="text-[11px] t-text-muted mb-1">
                    <b>{v.violation_label_id}</b> · {v.group_city} · {v.scope}
                  </div>
                  <div className="text-xs t-text bg-[var(--bg-elevated)] rounded p-2 italic break-words">&ldquo;{v.attempted_text}&rdquo;</div>
                </div>
              ))}
              {detailRows.length === 0 && <div className="text-xs t-text-muted text-center py-4">No details.</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
